import { useState } from 'react';
import type { LoanFinancialSnapshotV1, PayoffQuote } from './loan-financial.types';
import { formatBusinessDate, formatDateTimeAr, formatMoneyAr } from './loan-financial.format';
import {
  humanizePayoffApiError,
  payoffPolicyLabel,
  payoffQuoteStatusLabel,
} from './loan-financial.labels';
import { Hint, MoneyRow, SectionCard } from './LoanUiBits';
import { apiService } from '../../services/api.service';

type Props = {
  loanId: string;
  snap: LoanFinancialSnapshotV1;
  canQuote: boolean;
  canConfirm: boolean;
  loanStatus?: string;
  onRefresh: () => Promise<void> | void;
};

export function LoanPayoffPanel({
  loanId,
  snap,
  canQuote,
  canConfirm,
  loanStatus,
  onRefresh,
}: Props) {
  const [quote, setQuote] = useState<PayoffQuote | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const closed = loanStatus === 'COMPLETED' || loanStatus === 'CANCELLED' || loanStatus === 'REJECTED';
  const available = snap.payoff?.available && !closed;

  const requestQuote = async () => {
    setLoading(true);
    setError(null);
    setStale(false);
    try {
      const q = await apiService.createPayoffQuote(loanId);
      setQuote(q);
      setShowConfirm(false);
    } catch (e) {
      const h = humanizePayoffApiError(e);
      setError(h.message);
      setStale(Boolean(h.stale));
      setQuote(null);
    } finally {
      setLoading(false);
    }
  };

  const confirm = async () => {
    if (!quote?.quoteId || quote.status !== 'ACTIVE') return;
    setConfirming(true);
    setError(null);
    try {
      await apiService.confirmPayoffQuote(loanId, { quoteId: quote.quoteId });
      setShowConfirm(false);
      setQuote(null);
      await onRefresh();
    } catch (e) {
      const h = humanizePayoffApiError(e);
      setError(h.message);
      setStale(Boolean(h.stale));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <SectionCard
      title="Cancelación anticipada"
      subtitle="Cotización según política del préstamo. No confundir con el total contractual pendiente."
    >
      <p className="mb-3 text-xs text-slate-500">
        El monto de cancelación anticipada se calcula según la política del préstamo y puede diferir
        del plan contractual pendiente.
        <Hint text="Contractual incluye cuotas futuras; la cancelación anticipada usa la política (p. ej. interés a la fecha)." />
      </p>

      <div className="mb-3 grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <p className="text-xs text-slate-500">Total contractual pendiente</p>
          <p className="font-medium tabular-nums">{formatMoneyAr(snap.contractual.total)}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500">Política</p>
          <p className="font-medium">{payoffPolicyLabel(snap.payoff?.policy)}</p>
        </div>
      </div>

      {!available ? (
        <p className="text-sm text-slate-500">Cancelación anticipada no disponible para este estado.</p>
      ) : canQuote ? (
        <button
          type="button"
          onClick={() => void requestQuote()}
          disabled={loading}
          className="rounded-md bg-slate-800 px-3 py-2 text-sm text-white hover:bg-slate-900 disabled:opacity-50"
        >
          {loading ? 'Cotizando…' : 'Solicitar cotización'}
        </button>
      ) : (
        <p className="text-sm text-slate-500">Sin permiso para cotizar cancelación.</p>
      )}

      {error ? (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {error}
          {stale ? (
            <button type="button" className="ml-2 underline" onClick={() => void requestQuote()}>
              Generar nueva cotización
            </button>
          ) : null}
        </div>
      ) : null}

      {quote ? (
        <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 space-y-1.5">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-slate-900">
              Cancelación al {formatBusinessDate(quote.asOf)}
            </p>
            <span className="text-xs text-slate-500">
              {payoffQuoteStatusLabel(quote.status)} · válida hasta{' '}
              {formatDateTimeAr(quote.validUntil)}
            </span>
          </div>
          <MoneyRow label="Capital pendiente" value={formatMoneyAr(quote.principal)} />
          <MoneyRow label="Interés a cobrar" value={formatMoneyAr(quote.interest)} />
          {Number(quote.futureInterest) > 0 ? (
            <MoneyRow
              label="Interés futuro no cobrado"
              value={formatMoneyAr(quote.futureInterest)}
              tooltip="Interés futuro que deja de formar parte del saldo al cancelar (no es un descuento/waiver)."
            />
          ) : null}
          <MoneyRow label="Cargos aplicables" value={formatMoneyAr(quote.fees)} />
          <MoneyRow
            label="Punitorios"
            value={formatMoneyAr(quote.penalties ?? quote.lateFees)}
          />
          {Number(quote.earlyCancellationFee) > 0 ? (
            <MoneyRow
              label="Comisión cancelación"
              value={formatMoneyAr(quote.earlyCancellationFee)}
            />
          ) : null}
          {Number(quote.discounts) > 0 ? (
            <MoneyRow label="Descuentos" value={formatMoneyAr(quote.discounts)} />
          ) : null}
          <div className="border-t border-slate-200 pt-2">
            <MoneyRow label="TOTAL A CANCELAR" value={formatMoneyAr(quote.total)} emphasis />
          </div>

          {canConfirm && quote.status === 'ACTIVE' ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {!showConfirm ? (
                <button
                  type="button"
                  onClick={() => setShowConfirm(true)}
                  className="rounded-md bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"
                >
                  Confirmar cancelación…
                </button>
              ) : (
                <div className="w-full rounded-md border border-blue-200 bg-blue-50 p-3 space-y-2">
                  <p className="text-sm font-medium text-blue-900">Confirmación explícita</p>
                  <p className="text-xs text-blue-800">
                    Monto {formatMoneyAr(quote.total)} · Fecha {formatBusinessDate(quote.asOf)} ·
                    Quote válida hasta {formatDateTimeAr(quote.validUntil)}
                  </p>
                  <p className="text-xs text-blue-800">
                    Se usará el quoteId exacto de esta cotización (sin recalcular en pantalla).
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={confirming}
                      onClick={() => void confirm()}
                      className="rounded-md bg-blue-700 px-3 py-1.5 text-sm text-white disabled:opacity-50"
                    >
                      {confirming ? 'Confirmando…' : 'Confirmar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowConfirm(false)}
                      className="rounded-md px-3 py-1.5 text-sm text-slate-700 hover:bg-white"
                    >
                      Volver
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </SectionCard>
  );
}
